import { FC, useCallback, useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Portal } from '@mui/material';
import * as Sentry from '@sentry/capacitor';
import { Span } from '@sentry/core';
import invariant from 'tiny-invariant';

import {
	type ISetPaymentMethodParams,
	useBusinessLayer,
	usePaymentMethod,
} from '@mopla/business-logic';
import { StripeSetupIntentData } from '@mopla/data-models';
import { appendSearchParams } from '@mopla/utils';

import { usePaymentEnv } from '../../context/paymentEnvContext';
import { PaymentRestoreProvider } from '../../context/paymentRestoreContext';
import { useInternalStore } from '../../stores/useInternalStore';
import { usePaymentStepStore } from '../../stores/usePaymentStepStore';
import { usePaymentPortalControls } from '../../utils';
import { SETUP_INTENT_URL } from '../../utils/payByBank';
import { Payment } from '../Payment';

interface IPaymentPortalProps {
	restorePage: string;
	restoreQuery?: string;
	onDismiss?: VoidFunction;
}

export const PaymentPortal: FC<IPaymentPortalProps> = ({
	restorePage,
	restoreQuery,
	onDismiss,
}) => {
	const { stripeCustomCloseCallback, paymentBaseUrl } = usePaymentEnv();

	const stripeError = useInternalStore((s) => s.stripeError || undefined);
	const isTrueLayerEnabled = useInternalStore((s) => s.isTrueLayerEnabled);

	const [clientSecret, setClientSecret] = useState<string>();
	const [hasInitiated, setHasInitiated] = useState(false);
	const businessLayer = useBusinessLayer();
	const currentPaymentStep = usePaymentStepStore((state) => state.step);
	const { addStripePMHandler } = usePaymentMethod();

	const { closePaymentPortal } = usePaymentPortalControls();

	const passApi = businessLayer.api;

	useEffect(() => {
		const initiatePayment = async (span: Span) => {
			try {
				invariant(
					paymentBaseUrl,
					'[PaymentPortal]: initiatePayment: paymentBaseUrl is not set'
				);

				invariant(
					stripeCustomCloseCallback,
					'[PaymentPortal]: initiatePayment: stripeCustomCloseCallback is not set'
				);

				const response = await Sentry.startSpan(
					{
						op: 'http.client',
						name: 'getSetupIntent',
						attributes: {
							'http.method': 'GET',
							'http.url': SETUP_INTENT_URL,
						},
					},
					() => {
						return passApi.get<StripeSetupIntentData>(SETUP_INTENT_URL);
					}
				);

				const isWeb = Capacitor.getPlatform() === 'web';
				if (isWeb) {
					setClientSecret(response.stripeSetupIntentClientSecret);
					span.setAttribute('isWeb', 'true');
				} else {
					span.setAttribute('isWeb', 'false');
					const url = appendSearchParams(paymentBaseUrl, {
						clientSecret: response.stripeSetupIntentClientSecret,
						closeCallback: stripeCustomCloseCallback,
					});

					await Browser.open({ url });
				}
				span.setStatus({ code: 1, message: 'Payment setup successful' });
			} catch (err) {
				span.setStatus({ code: 2, message: 'Payment setup failed' });
				Sentry.captureException(err);
				console.error(err);
			}
		};

		if (currentPaymentStep === 'stripe' && !hasInitiated) {
			Sentry.startSpan(
				{
					name: 'addPaymentMethod',
					op: 'payment.setup',
					attributes: {
						step: currentPaymentStep,
						isPayment: true,
					},
				},
				initiatePayment
			);
			setHasInitiated(true);
		}
	}, [
		hasInitiated,
		passApi,
		currentPaymentStep,
		isTrueLayerEnabled,
		paymentBaseUrl,
		stripeCustomCloseCallback,
	]);

	const onStripeCardFlowCompleted = useCallback(
		(payload: ISetPaymentMethodParams) => {
			/**
			 * When adding a Stripe Card PM, we immediately close the PaymentPortal.
			 * The loading state of the new PM will not be displayed in the PaymentPortal.
			 * If needed, it can be displayed in the parent component.
			 * */
			closePaymentPortal();

			addStripePMHandler(payload);
		},
		[addStripePMHandler, closePaymentPortal]
	);

	return (
		<Portal>
			<PaymentRestoreProvider
				restorePage={restorePage}
				restoreQuery={restoreQuery}
			>
				<Payment
					onCancel={closePaymentPortal}
					onDismiss={onDismiss}
					stripeClientSecret={clientSecret}
					stripeError={stripeError}
					onStripeCardFlowCompleted={onStripeCardFlowCompleted}
					onStripeFlowClose={closePaymentPortal}
					stripeElementOptions={{}}
				/>
			</PaymentRestoreProvider>
		</Portal>
	);
};
